import { Inject, Injectable, Injector } from '@angular/core'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { TranslateService } from '@ngx-translate/core'
import { firstValueFrom, Subject } from 'rxjs'
import { APIService } from 'api/module'
import { BOOTSTRAP_DATA, LAZY_LOADER } from 'api/providers'
import { Logger } from 'common/log'
import { PickerService } from 'common/util.pickers/module'
import { MessageBoxService, SocketService } from 'common/util/module'
import { BootstrapData, Job, Subtask, TaskInfo, TaskInfoFromJSON, TaskProgressFromJSON, TaskType } from 'elements-sdk'
import { IdentityService } from 'main/services/identity.service'
import { NotifyService } from 'common/util/services/notify.service'
import type { LazyLoaderService } from 'ng1'

export enum TaskState {
  PENDING = 0,
  RUNNING = 1,
  FINISHED = 2,
  ABORTING = 3,
  ABORTED = 4,
  FAILED = 5,
  WARNINGS = 6,
  NOOP = 7,
}

@Injectable({ providedIn: 'root' })
export class TasksService {
  tasksUpdated$ = new Subject<void>()
  tasks: TaskInfo[] = []
  finishedTasks: TaskInfo[] = []
  recentFinishedTasks: TaskInfo[] = []
  pendingTasks: TaskInfo[] = []
  pendingCount = 0
  activeTasks = 0

  types: Record<string, TaskType|undefined>
  taskStateNames: Record<TaskState, string>

  private lastUpdate = 0

  private logger = new Logger(__filename)
  private refreshRequested = false
  private taskCompleted$ = new Subject<TaskInfo>()

  constructor (
    @Inject(BOOTSTRAP_DATA) private bootstrapData: BootstrapData,
    private ngbModal: NgbModal,
    private api: APIService,
    private socket: SocketService,
    private identity: IdentityService,
    private picker: PickerService,
    private translate: TranslateService,
    private messageBox: MessageBoxService,
    private notify: NotifyService,
    private injector: Injector,
  ) {
    this.initConstants()

    this.recentFinishedTasks = []

    if (bootstrapData.tasksSummary) {
      this.pendingCount = bootstrapData.tasksSummary.pendingCount
      this.recentFinishedTasks = bootstrapData.tasksSummary.recentFinished
      this.applyTaskList(bootstrapData.tasksSummary.running)
    }

    setInterval(() => {
      if (this.refreshRequested) {
        this.refresh()
      }
      this.refreshRequested = false
    }, 1000)

    socket.subject('tasks:refresh').subscribe(() => {
      this.refreshRequested = true
    })

    socket.subject('tasks:list').subscribe(data => {
      this.pendingCount = data.pending_count
      this.applyTaskList(data.running.map(TaskInfoFromJSON))
    })

    socket.subject('tasks:progress').subscribe(data => {
      const task = this.tasks.find(x => x.id === data.id)
      if (task) {
        task.progress = TaskProgressFromJSON(data)
      }
    })

    socket.subject('tasks:done').subscribe(task => {
      const data = TaskInfoFromJSON(task)
      this.taskCompleted$.next(data)

      if (identity.user && data.user?.id === identity.user.id) {
        const levelMap = {
          [TaskState.ABORTED]: 'error',
          [TaskState.FAILED]: 'error',
          [TaskState.WARNINGS]: 'warning',
        }
        const textMap = {
          [TaskState.FINISHED]: translate.instant('Task has succeeded'),
          [TaskState.ABORTED]: translate.instant('Task was aborted'),
          [TaskState.FAILED]: translate.instant('Task has failed'),
          [TaskState.NOOP]: translate.instant('Task has finished'),
          [TaskState.WARNINGS]: translate.instant('Task has finished with warnings'),
        }

        const level = levelMap[data.state!]
        const text = textMap[data.state!] || textMap[TaskState.NOOP]

        if (level) {
          this.notify.custom(
            level,
            text,
            data.name ?? this.types[data.taskName ?? '']?.displayName,
            { url: `/tasks/report/${data.id}` },
          )
        }

        if (!this.recentFinishedTasks.some(x => x.id === data.id)) {
          this.recentFinishedTasks.unshift(data)
        }
      }
    })

    socket.reconnected$.subscribe(() => {
      this._forceRefresh()
    })
  }

  refresh (): void {
    if (this.socket.isConnected()) {
      this.socket.send({ type: 'tasks:query' })
    } else {
      setTimeout(() => {
        this._forceRefresh()
      })
    }
  }

  async _forceRefresh (): Promise<void> {
    const summary = await this.api.automation.getTasksSummary({})
    this.pendingCount = summary.pendingCount
    this.applyTaskList(summary.running)
  }

  async refreshFinished (): Promise<void> {
    this.finishedTasks = await this.api.automation.getFinishedTasks({})
    const deadline = new Date(Date.now() - 3600000)
    this.recentFinishedTasks = this.finishedTasks.filter(x => x.finished! > deadline)
    this.tasksUpdated$.next()
  }

  async refreshPending (): Promise<void> {
    this.pendingTasks = await this.api.automation.getPendingTasks({})
  }

  applyTaskList (list: TaskInfo[]): void {
    this.tasks = list
    this.activeTasks = 0
    this.logger.log('TaskInfo list:', this.tasks)
    for (const task of this.tasks) {
      if (task.state === TaskState.RUNNING as any) {
        this.activeTasks += 1
      }
    }
    this.lastUpdate = Date.now()
    this.tasksUpdated$.next()
  }

  async abort (task: TaskInfo): Promise<void> {
    const meta = this.types[task.taskName ?? '']
    if (meta && !meta.abortable) {
      return this.kill(task)
    }
    (task as any).state = TaskState.ABORTING
    await this.api.automation.abortTask(task)
  }

  async kill (task: TaskInfo): Promise<void> {
    this.pendingTasks = this.pendingTasks.filter(x => x !== task)
    this.tasks = this.tasks.filter(x => x !== task)
    await this.api.automation.killTask(task)
  }

  async restart (task: TaskInfo): Promise<TaskInfo> {
    const taskInfo = await this.api.automation.restartTask(task)
    this.refresh()
    return taskInfo
  }

  async remove (task: TaskInfo): Promise<void> {
    this.finishedTasks = this.finishedTasks.filter(x => x !== task)
    await this.api.automation.deleteTask(task)
    this.tasksUpdated$.next()
  }

  async removeAll (): Promise<void> {
    try {
      await this.messageBox.show({
        text: this.translate.instant('Remove all tasks and reports?'),
        positive: this.translate.instant('Delete'),
        negative: this.translate.instant('Cancel'),
        danger: true,
      })
    } catch {
      return
    }
    this.finishedTasks = []
    this.tasksUpdated$.next()
    await this.api.automation.deleteFinishedTasks()
  }

  async killPending (): Promise<void> {
    try {
      await this.messageBox.show({
        text: this.translate.instant('Kill all pending tasks?'),
        positive: this.translate.instant('Kill'),
        negative: this.translate.instant('Cancel'),
        danger: true,
      })
    } catch {
      return
    }
    await this.api.automation.killAllPendingTasks()
    this.pendingTasks = []
  }

  async start (
    taskType: string,
    parameters: Record<string, any>,
    options?: {
      sync: boolean,
    },
  ): Promise<TaskInfo> {
    const taskInfo = await this.api.automation.startTask({
      startTaskRequest: {
        taskType,
        parameters,
        sync: options?.sync,
      },
    })
    this.refresh()
    return taskInfo
  }

  async startJob (job: Job, variables: Record<string, any>): Promise<TaskInfo[]> {
    let tasks: TaskInfo[] = []
    if (job.variableDefinitions?.length) {
      // eslint-disable-next-line import/no-cycle
      const { StartJobModalComponent } = await import('../components/startJobModal.component')
      const m = this.ngbModal.open(StartJobModalComponent)
      m.componentInstance.job = job
      m.componentInstance.vars = variables
      try {
        tasks = await m.result
      } catch {
        return []
      }
    } else {
      tasks = await this.startJobRaw(job, variables)
    }
    this.notify.success(this.translate.instant('Started'))
    this.refresh()
    return tasks
  }

  async startJobRaw (job: Job, variables: Record<string, any>): Promise<TaskInfo[]> {
    return this.api.automation.startJob({
      id: job.id,
      startJobRequest: {
        variables,
      },
    })
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async createJobFromTemplate (template: any, vars?: Record<string, any>): Promise<Job> {
    const job = await this.api.automation.createJob({
      jobUpdate: template.job,
    })

    const subtasks: Subtask[] = []
    for (const st of template.subtasks(vars ?? {})) {
      const subtask = {
        ...st,
        parent: job.id,
        relativeTo: subtasks[st.relativeTo]?.id,
      }
      subtasks.push(await this.api.automation.createSubtask({
        subtaskUpdate: subtask,
      }))
    }
    return job
  }

  async getTaskTypes (): Promise<TaskType[]> {
    const values = await this.api.automation.getAllTaskTypes({})
    values.sort((a, b) => a.displayName > b.displayName ? 1 : -1)
    return values
  }

  pickTask (): Promise<string> {
    return this.picker.pickObject({
      items: this.getTaskTypes().then(types =>
        types
          .filter(x => x.allowInJobs && x.isAvailable)
          .map(task => ({
            name: task.displayName,
            group: task.displayGroup,
            iconClass: task.iconClass ?? 'far fa-cog',
            object: task.type,
            newSinceVersion: task.newSinceVersion,
          })),
      ),
      groupOrder: [
        this.translate.instant('Scripting'),
        this.translate.instant('Filesystem'),
        this.translate.instant('Notifications'),
        this.translate.instant('General'),
        this.translate.instant('Media Library'),
      ],
    })
  }

  async waitTasksComplete (ids: string[]): Promise<TaskInfo[]> {
    let remainingTaskIds = ids
    const results: TaskInfo[] = []

    const suddenlyFinished$ = new Subject<void>()

    // Wait for either a push or the manual check
    // to avoid race conditions
    ;(async () => {
      for (const id of remainingTaskIds) {
        const task = await this.api.automation.getTask({ id }).catch(() => null)
        if (!task || ![TaskState.PENDING, TaskState.RUNNING].includes(task.state as any)) {
          suddenlyFinished$.next()
          if (!task) {
            remainingTaskIds = remainingTaskIds.filter(t => t !== id)
          }
          if (task && remainingTaskIds.includes(id)) {
            this.taskCompleted$.next(task)
          }
        }
      }
    })()

    while (remainingTaskIds.length) {
      const task = await Promise.race([
        firstValueFrom(this.taskCompleted$),
        firstValueFrom(suddenlyFinished$),
      ])
      if (task) {
        remainingTaskIds = remainingTaskIds.filter(t => t !== task.id)
        results.push(task)
      }
    }

    return results
  }

  private initConstants () {
    this.injector.get<LazyLoaderService>(LAZY_LOADER).loaded$.subscribe($injector => {
      const $templateCache = $injector.get('$templateCache')
      this.types = this.bootstrapData.taskMeta
      for (const [task, meta] of Object.entries(this.types) as [string, TaskType][]) {
        if (meta.displayName) {
          meta.displayName = this.translate.instant(meta.displayName)
        }
        if (meta.displayGroup) {
          meta.displayGroup = this.translate.instant(meta.displayGroup)
        }

        if (meta.parametersEditorComponent) {
          const c = meta.parametersEditorComponent
          const template = `<${c} args='$ctrl.subtask.kwargs' subtask='$ctrl.subtask' job='$ctrl.job' root='$ctrl.root'></${c}>`
          $templateCache.put(`/task-params:${task}`, template)
        } else {
          $templateCache.put(`/task-params:${task}`, '')
        }
      }
    })

    this.taskStateNames = [
      this.translate.instant('PENDING'),
      this.translate.instant('RUNNING'),
      this.translate.instant('FINISHED'),
      this.translate.instant('ABORTING'),
      this.translate.instant('ABORTED'),
      this.translate.instant('FAILED'),
      this.translate.instant('WARNINGS'),
      this.translate.instant('NOOP'),
    ]
  }
}
